/**
 * ContactPage — /contact
 *
 * A proper destination for the old /contact URL Google was indexing.
 * Left column: office details, phone, email, sales/support/backorder rails.
 * Right column: message form (mailto submission until we wire a real endpoint).
 * Bottom: Houston TMC map embed + service-area callouts (TX · LA · AL · MS).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Building2,
  MessageSquare,
  ShoppingCart,
  ShieldCheck,
  ExternalLink,
  Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SiteHeader from "@/components/SiteHeader";
import SEO from "@/components/SEO";

const OFFICE = {
  address_line: "396 W Greens Road, Suite 1139",
  city_state_zip: "Houston, TX 77067",
  phone: "800.988.3892",
  phone_href: "tel:8009883892",
  orders: "orders@sterilemate.com",
  support: "support@sterilemate.com",
  hours: "Mon – Fri · 8:00 AM – 6:00 PM CST",
  map_query: "396 W Greens Road Suite 1139, Houston, TX 77067",
};

const CONTACT_RAILS = [
  {
    icon: ShoppingCart,
    color: "cyan",
    label: "Sales & Quotes",
    body: "Product pricing, custom quotes, Net-30 terms, bulk pricing on contract items.",
    email: "orders@sterilemate.com",
    subject: "Sterile Mate — Sales inquiry",
  },
  {
    icon: ShieldCheck,
    color: "emerald",
    label: "Backorders & Sourcing",
    body: "Discontinued SKUs, foreign catalogs, emergency same-day sourcing.",
    email: "backorders@sterilemate.com",
    subject: "Sterile Mate — Backorder sourcing request",
  },
  {
    icon: MessageSquare,
    color: "amber",
    label: "Support & Account",
    body: "Existing orders, invoices, shipment tracking, portal & account questions.",
    email: "support@sterilemate.com",
    subject: "Sterile Mate — Support request",
  },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", org: "", email: "", phone: "", topic: "Sales & Quotes", message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    const subject = `Sterile Mate contact form — ${form.topic}`;
    const body = [
      `Name: ${form.name}`,
      `Organization: ${form.org}`,
      `Email: ${form.email}`,
      `Phone: ${form.phone}`,
      `Topic: ${form.topic}`,
      "",
      form.message,
    ].join("\n");
    window.location.href = `mailto:${OFFICE.orders}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contact-page">
      <SEO title="Contact Sterile Mate — Hospital Supply Sourcing (Houston TX)" />
      <SiteHeader />

      {/* Hero */}
      <header className="bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-14 lg:py-20">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 border border-white/20 rounded-full mb-4">
            <Phone className="w-3.5 h-3.5" />
            <span className="text-[11px] font-black uppercase tracking-widest">Contact Sterile Mate</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] max-w-3xl">
            Talk to a real human. <span className="text-cyan-300">No phone tree, no ticket queue.</span>
          </h1>
          <p className="mt-4 text-lg text-white/80 max-w-2xl">
            Serving hospitals, ASCs, dental practices, and dialysis clinics across Texas, Louisiana, Alabama, and Mississippi from our Houston headquarters.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href={OFFICE.phone_href} className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-white text-slate-900 font-bold text-sm hover:bg-slate-100 transition-all" data-testid="contact-phone-cta">
              <Phone className="w-4 h-4" /> {OFFICE.phone}
            </a>
            <a href={`mailto:${OFFICE.orders}`} className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-white/30 text-white font-bold text-sm hover:bg-white/10 transition-all" data-testid="contact-email-cta">
              <Mail className="w-4 h-4" /> {OFFICE.orders}
            </a>
          </div>
        </div>
      </header>

      {/* Two-column body: rails + form */}
      <main className="max-w-7xl mx-auto px-6 py-12 grid lg:grid-cols-5 gap-10">
        {/* Left — office + rails */}
        <aside className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-5 h-5 text-slate-700" />
              <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Headquarters</h2>
            </div>
            <p className="text-slate-900 font-bold">Sterile Mate</p>
            <p className="text-slate-700">{OFFICE.address_line}</p>
            <p className="text-slate-700">{OFFICE.city_state_zip}</p>
            <p className="text-slate-700">{OFFICE.city_state_zip}</p>
            <div className="border-t border-slate-100 mt-4 pt-4 space-y-2 text-sm">
              <p className="flex items-center gap-2 text-slate-800"><Phone className="w-4 h-4 text-slate-500" /> {OFFICE.phone}</p>
              <p className="flex items-center gap-2 text-slate-800"><Mail className="w-4 h-4 text-slate-500" /> {OFFICE.orders}</p>
              <p className="flex items-center gap-2 text-slate-800"><Clock className="w-4 h-4 text-slate-500" /> {OFFICE.hours}</p>
              <p className="flex items-center gap-2 text-slate-800"><MapPin className="w-4 h-4 text-slate-500" /> North Houston · Greenspoint corridor</p>
            </div>
          </div>

          <div className="space-y-3">
            {CONTACT_RAILS.map((r) => (
              <a
                key={r.email}
                href={`mailto:${r.email}?subject=${encodeURIComponent(r.subject)}`}
                className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-slate-400 hover:shadow transition-all"
                data-testid={`contact-rail-${r.color}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg bg-${r.color}-100 flex items-center justify-center shrink-0`}>
                    <r.icon className={`w-5 h-5 text-${r.color}-700`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-900">{r.label}</p>
                    <p className="text-xs text-slate-600 mt-0.5 leading-snug">{r.body}</p>
                    <p className="text-xs font-mono text-slate-700 mt-1 truncate">{r.email}</p>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </aside>

        {/* Right — form */}
        <section className="lg:col-span-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
            <h2 className="text-2xl font-black text-slate-900 mb-1">Send us a message</h2>
            <p className="text-sm text-slate-600 mb-6">Every message is read by a real Sterile Mate team member — usually a same-day reply.</p>

            <form onSubmit={onSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5" htmlFor="name">Full name</label>
                  <Input id="name" value={form.name} onChange={set("name")} required placeholder="Jane Buyer" className="h-11 text-base" autoComplete="name" autoCapitalize="words" data-testid="contact-form-name" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5" htmlFor="org">Facility / hospital</label>
                  <Input id="org" value={form.org} onChange={set("org")} placeholder="Memorial Hermann · SPD" className="h-11 text-base" autoComplete="organization" autoCapitalize="words" data-testid="contact-form-org" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5" htmlFor="email">Email</label>
                  <Input id="email" type="email" value={form.email} onChange={set("email")} required placeholder="you@hospital.org" className="h-11 text-base" autoComplete="email" inputMode="email" autoCapitalize="off" autoCorrect="off" spellCheck={false} data-testid="contact-form-email" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5" htmlFor="phone">Phone</label>
                  <Input id="phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="(713) 555-0139" className="h-11 text-base" autoComplete="tel" inputMode="tel" data-testid="contact-form-phone" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5" htmlFor="topic">Topic</label>
                <select
                  id="topic"
                  value={form.topic}
                  onChange={set("topic")}
                  className="w-full h-11 rounded-md border border-slate-300 bg-white px-3 text-base focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                  data-testid="contact-form-topic"
                >
                  <option>Sales &amp; Quotes</option>
                  <option>Backorders &amp; Sourcing</option>
                  <option>Net-30 Terms Request</option>
                  <option>Support &amp; Account</option>
                  <option>General Inquiry</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1.5" htmlFor="message">Message</label>
                <textarea
                  id="message"
                  value={form.message}
                  onChange={set("message")}
                  rows={6}
                  required
                  placeholder="Item numbers, quantity needed, delivery window, PO number, or anything else that helps us quote you accurately."
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-base focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100"
                  data-testid="contact-form-message"
                />
              </div>
              <Button type="submit" className="w-full h-12 bg-cyan-700 hover:bg-cyan-800 text-white text-sm font-bold shadow" data-testid="contact-form-submit">
                <Send className="w-4 h-4 mr-2" /> Send message
              </Button>
              <p className="text-[11px] text-slate-500 text-center">
                Submitting opens your default email client with your message pre-filled. Prefer to skip the middleman?{" "}
                <a href={`mailto:${OFFICE.orders}`} className="underline hover:text-slate-700">Email us directly</a>.
              </p>
            </form>
          </div>
        </section>
      </main>

      {/* Map + service area */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-12 grid lg:grid-cols-5 gap-10 items-start">
          <div className="lg:col-span-2 space-y-4">
            <h2 className="text-2xl font-black text-slate-900">Houston HQ · Greenspoint corridor</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              We ship U.S.-wide, but our roots are Houston. Same-day pickup available for TMC hospitals; next-day trucks run TX / LA / AL / MS on standing routes.
            </p>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-2">Service coverage</p>
              <div className="flex flex-wrap gap-1.5">
                {["Texas", "Louisiana", "Alabama", "Mississippi"].map((s) => (
                  <span key={s} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white border border-slate-200 text-xs font-semibold text-slate-700">
                    <MapPin className="w-3 h-3 text-cyan-600" /> {s}
                  </span>
                ))}
              </div>
            </div>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(OFFICE.map_query)}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm font-bold text-cyan-700 hover:text-cyan-800"
              data-testid="contact-open-in-maps"
            >
              Open in Google Maps <ExternalLink className="w-4 h-4" />
            </a>
          </div>
          <div className="lg:col-span-3">
            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
              <iframe
                title="Sterile Mate Houston HQ"
                src={`https://www.google.com/maps?q=${encodeURIComponent(OFFICE.map_query)}&output=embed`}
                width="100%"
                height="380"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="border-0"
                data-testid="contact-map-iframe"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-lg font-black">Skip the form — call, email, or shop the catalog.</p>
            <p className="text-sm text-white/70">All three funnel to the same team.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href={OFFICE.phone_href} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white text-slate-900 text-sm font-bold hover:bg-slate-100">
              <Phone className="w-4 h-4" /> {OFFICE.phone}
            </a>
            <a href={`mailto:${OFFICE.orders}`} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-white/30 text-white text-sm font-bold hover:bg-white/10">
              <Mail className="w-4 h-4" /> {OFFICE.orders}
            </a>
            <Link to="/marketplace" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-bold">
              <ShoppingCart className="w-4 h-4" /> Browse Marketplace
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
